import React from 'react';
import { ApiCategory, ApiWorkflow } from '../../hooks/useWorkflowApi';
import { WorkflowCard } from './WorkflowCard';

interface CategoryAccordionProps {
  category: ApiCategory;
  workflows: ApiWorkflow[];
  expanded: boolean;
  onToggle: () => void;
  onViewWorkflow: (workflow: ApiWorkflow) => void;
  onEditWorkflow: (workflow: ApiWorkflow) => void;
  onJourneyMap: (workflow: ApiWorkflow) => void;
  onToggleStatus: (workflow: ApiWorkflow) => void;
  onDeleteWorkflow: (workflow: ApiWorkflow) => void;
  canEdit: boolean;
}

export function CategoryAccordion({
  category,
  workflows,
  expanded,
  onToggle,
  onViewWorkflow,
  onEditWorkflow,
  onJourneyMap,
  onToggleStatus,
  onDeleteWorkflow,
  canEdit,
}: CategoryAccordionProps) {
  const headerId = `category-header-${category.id}`;
  const regionId = `category-region-${category.id}`;

  return (
    <div style={styles.container}>
      {/* 5.2: Header button with aria-expanded, icon, name, count, chevron */}
      <button
        id={headerId}
        type="button"
        aria-expanded={expanded}
        aria-controls={regionId}
        onClick={onToggle}
        style={styles.header}
      >
        <span style={styles.icon}>{category.icon}</span>
        <span style={styles.name}>{category.name}</span>
        <span style={styles.count}>{workflows.length}</span>
        <span style={styles.chevron}>{expanded ? '▼' : '▶'}</span>
      </button>

      {/* 5.3: Content region with role="region" and aria-labelledby */}
      {/* 5.4: Expand/collapse via CSS max-height transition (300ms ease) */}
      <div
        id={regionId}
        role="region"
        aria-labelledby={headerId}
        style={{
          ...styles.content,
          maxHeight: expanded ? '9999px' : '0',
        }}
      >
        <div style={styles.inner}>
          {/* 5.5: Render WorkflowCards when expanded */}
          {workflows.length > 0 ? (
            workflows.map(workflow => (
              <WorkflowCard
                key={workflow.id}
                workflow={workflow}
                onView={() => onViewWorkflow(workflow)}
                onEdit={() => onEditWorkflow(workflow)}
                onJourneyMap={() => onJourneyMap(workflow)}
                onToggleStatus={() => onToggleStatus(workflow)}
                onDelete={() => onDeleteWorkflow(workflow)}
                canEdit={canEdit}
              />
            ))
          ) : (
            /* 5.6: Empty state message when expanded and no workflows */
            <p style={styles.emptyMessage}>No workflows in this category</p>
          )}
        </div>
      </div>
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    marginBottom: '12px',
    borderRadius: '8px',
    border: '1px solid #e0e0e0',
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    width: '100%',
    padding: '12px 16px',
    border: 'none',
    backgroundColor: '#f8f8f8',
    cursor: 'pointer',
    fontSize: '15px',
    fontWeight: 600,
    color: '#1a1a1a',
    textAlign: 'left',
  },
  icon: {
    fontSize: '20px',
    flexShrink: 0,
  },
  name: {
    flex: '1 1 auto',
  },
  count: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    minWidth: '22px',
    height: '22px',
    padding: '0 6px',
    borderRadius: '9999px',
    backgroundColor: '#1a6b3c',
    color: '#fff',
    fontSize: '12px',
    fontWeight: 600,
    flexShrink: 0,
  },
  chevron: {
    fontSize: '12px',
    flexShrink: 0,
    color: '#666',
  },
  content: {
    overflow: 'hidden',
    transition: 'max-height 300ms ease',
  },
  inner: {
    padding: '12px 16px',
  },
  emptyMessage: {
    margin: 0,
    padding: '16px 0',
    textAlign: 'center',
    color: '#888',
    fontSize: '14px',
    fontStyle: 'italic',
  },
};
